import { Button } from '@curology/ui-components-web/react/Button';
import React, { useCallback } from 'react';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import CreditCardInput from '@/components/payments/components/creditCardInput';
import { useStripe } from '@/components/payments/hooks/useStripe';
import { type CHECKOUT_CONTEXT } from '@/consts/checkout';
import { PAYMENT_TYPES, PROCESSOR_NAMES } from '@/consts/payments';
import { PaymentMethodService } from '@/services/paymentsService/client';
import {
  type NewPaymentMethod,
  type RegisterPaymentMethodData,
} from '@/types/payments/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

/**
 * The form confirms the new card in Stripe, and
 * registers the card in the backend.
 *
 * @prop onSuccess Executed on the successfully registered payment method.
 * @prop onError Executed on any error message in the workflow.
 * @prop onRegister Optional. Executed just before registering.
 * @prop checkoutContext Optional. Indicates context if rendering in a checkout flow.
 */
const RegisterCreditCardPaymentMethodForm = ({
  onSuccess,
  onError,
  onRegister = () => {},
  checkoutContext,
  validateOnly = false,
}: {
  onSuccess: (paymentMethod: NewPaymentMethod) => void;
  onError: (message: string) => void;
  onRegister?: () => void;
  checkoutContext?: valueof<typeof CHECKOUT_CONTEXT>;
  validateOnly?: boolean;
}) => {
  const { brand } = useFlowContext();
  const Stripe = useStripe();

  const getCreditCardPaymentMethod = (card?: stripe.Card): NewPaymentMethod => {
    return {
      cardBrand: card?.brand,
      cardFundingType: card?.funding,
      cardLastFour: card?.last4,
      processor: PROCESSOR_NAMES.STRIPE,
      type: PAYMENT_TYPES.CREDIT_CARD,
    };
  };

  const formatNewPaymentMethodToData = (
    newPaymentMethod: NewPaymentMethod,
    paymentNonce: string,
    checkoutContext?: valueof<typeof CHECKOUT_CONTEXT>,
  ): RegisterPaymentMethodData => {
    const { cardLastFour, cardFundingType } = newPaymentMethod;

    return {
      method: newPaymentMethod.type,
      paymentNonce,
      vendor: newPaymentMethod.processor,
      ...(cardLastFour ? { cardLastFour } : {}),
      ...(cardFundingType ? { cardFundingType } : {}),
      ...(checkoutContext ? { checkoutContext } : {}),
    };
  };

  const onStorePaymentSuccess = (newPaymentMethod: NewPaymentMethod) => {
    trackEvent(AnalyticsEvent.CARD_SAVED, {
      card_type: newPaymentMethod.cardFundingType || '',
    });
    onSuccess(newPaymentMethod);
  };

  const onStorePaymentError = (message: string) => {
    trackEvent(AnalyticsEvent.CREDIT_CARD_VALIDATION_ERROR);
    onError(message);
  };

  const confirmCreditCardPayment = useCallback(async () => {
    const storePayment = async (token: stripe.Token) => {
      const newPaymentMethod = getCreditCardPaymentMethod(token.card);

      if (validateOnly) {
        return onStorePaymentSuccess({
          ...newPaymentMethod,
          paymentNonce: token.id,
        });
      }

      await PaymentMethodService.storePaymentMethod({
        brand,
        data: formatNewPaymentMethodToData(
          newPaymentMethod,
          token.id,
          checkoutContext,
        ),
        onSuccess: () => onStorePaymentSuccess(newPaymentMethod),
        onError: (message) => onStorePaymentError(message),
      });
    };

    await Stripe.confirmCcPayment({ onSuccess: storePayment, onError });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand, checkoutContext, onSuccess, onError, Stripe]);

  return (
    <>
      <CreditCardInput />
      <Button
        className="cur-w-full cur-mt-4"
        data-sentry-id="Submit Stripe CC form"
        onClick={() => {
          onRegister();
          confirmCreditCardPayment();
        }}
        size="lg"
        disabled={!Stripe.canConfirmCcPayment}
        loading={Stripe.ccFormSubmitting}
      >
        Continue
      </Button>
    </>
  );
};

export default RegisterCreditCardPaymentMethodForm;
